import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import {
  Container, Paper, Typography, TextField, Button, Box,
  Alert, Tabs, Tab, Divider, Chip, Card, CardContent,
  LinearProgress, List, ListItem, ListItemText
} from '@mui/material';
import {
  CloudUpload, Preview, Send, CheckCircle, Error, Image
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';

const ImportTickets = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [emailContent, setEmailContent] = useState('');
  const [csvContent, setCsvContent] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [pastedImages, setPastedImages] = useState([]);
  const contentEditableRef = React.useRef(null);

  const handleEmailPreview = async () => {
    if (!emailContent.trim()) {
      toast.error('Bitte fügen Sie E-Mail-Inhalt ein');
      return;
    }

    setLoading(true);
    try {
      // Der emailContent enthält bereits HTML mit Bildern
      const response = await api.post('/import/preview', { emailContent });
      setPreview(response.data);
    } catch (error) {
      toast.error('Fehler beim Parsen der E-Mail');
    }
    setLoading(false);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    
    // Hole HTML-Inhalt aus der Zwischenablage
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    
    if (html) {
      // Verwende sanitisierten HTML-Inhalt (enthält Bilder)
      const sanitizedHtml = DOMPurify.sanitize(html, { ADD_TAGS: ['img'], ADD_ATTR: ['src'] });
      setEmailContent(sanitizedHtml);

      // Extrahiere Bilder aus dem HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = sanitizedHtml;
      const images = tempDiv.querySelectorAll('img');
      
      const extractedImages = [];
      images.forEach((img, index) => {
        if (img.src && img.src.startsWith('data:image')) {
          extractedImages.push({
            dataUrl: img.src,
            name: `image_${index + 1}.png`,
            type: 'image/png',
            size: 0
          });
        }
      });
      
      if (extractedImages.length > 0) {
        setPastedImages(extractedImages);
      }
    } else if (text) {
      // Fallback auf reinen Text
      setEmailContent(text);
    }
    
    // Prüfe auch auf direkt eingefügte Bilder
    const items = e.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf('image') !== -1) {
          const blob = item.getAsFile();
          if (blob) {
            const reader = new FileReader();
            reader.onload = (event) => {
              const dataUrl = event.target.result;
              setPastedImages(prev => [...prev, {
                dataUrl,
                name: `clipboard_image_${Date.now()}.png`,
                type: blob.type,
                size: blob.size
              }]);
            };
            reader.readAsDataURL(blob);
          }
        }
      }
    }
  };

  const removeImage = (index) => {
    setPastedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleEmailImport = async () => {
    if (!emailContent.trim()) {
      toast.error('Bitte fügen Sie E-Mail-Inhalt ein');
      return;
    }

    setLoading(true);
    try {
      // Bereite den E-Mail-Inhalt vor - entferne gelöschte Bilder
      let processedContent = emailContent;
      
      // Wenn Bilder gelöscht wurden, entferne sie aus dem HTML
      if (pastedImages.length === 0 && emailContent.includes('data:image')) {
        // Alle Bilder wurden gelöscht - entferne sie aus dem HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = emailContent;
        const images = tempDiv.querySelectorAll('img[src^="data:image"]');
        images.forEach(img => img.remove());
        processedContent = tempDiv.innerHTML;
      } else if (pastedImages.length > 0) {
        // Füge nur die nicht gelöschten Bilder ein
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = emailContent;
        const images = tempDiv.querySelectorAll('img[src^="data:image"]');
        
        // Entferne alle Bilder erstmal
        images.forEach(img => img.remove());
        
        // Füge nur die pastedImages wieder ein
        pastedImages.forEach(img => {
          const imgElement = document.createElement('img');
          imgElement.src = img.dataUrl;
          tempDiv.appendChild(imgElement);
        });
        
        processedContent = tempDiv.innerHTML;
      }
      
      const response = await api.post('/import/email', { 
        emailContent: processedContent,
        selectedImages: pastedImages 
      });
      
      const attachmentCount = response.data.attachments ? response.data.attachments.length : 0;
      const successMessage = attachmentCount > 0 
        ? `Ticket ${response.data.ticket.id} mit ${attachmentCount} Anhängen erstellt`
        : `Ticket ${response.data.ticket.id} erfolgreich erstellt`;
      
      toast.success(successMessage);
      setImportResults({
        success: true,
        ticketId: response.data.ticket.id,
        title: response.data.ticket.title,
        attachments: response.data.attachments
      });
      
      // Lösche die eingefügten Bilder nach erfolgreichem Import
      setPastedImages([]);
      setEmailContent('');
      
      // Nach 2 Sekunden zum Ticket navigieren
      setTimeout(() => {
        navigate(`/tickets/${response.data.ticket.id}`);
      }, 2000);
    } catch (error) {
      toast.error('Fehler beim Import');
      setImportResults({ success: false, error: error.message });
    }
    setLoading(false);
  };

  const handleCSVImport = async () => {
    if (!csvContent.trim()) {
      toast.error('Bitte fügen Sie CSV-Daten ein');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/import/csv', { csvContent });
      setImportResults({
        success: true,
        created: response.data.created,
        failed: response.data.failed,
        errors: response.data.errors
      });
      toast.success(`${response.data.created} Tickets importiert`);
      
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} Tickets konnten nicht importiert werden`);
      }
    } catch (error) {
      toast.error('Fehler beim CSV-Import');
      setImportResults({ success: false, error: error.message });
    }
    setLoading(false);
  };

  const sampleEmail = `Hallo Kollegen, 
bitte um Entstörung des PLC-Repeater mit der Seriennummer C100810230208019.

Ticketnummer: 20250806-0791
Meldedatum: 06.08.2025 15:01:49
Seriennummer: C100810230208019
Geräteklasse: PLC-Repeater
Gerätetyp: CXP-UFSGW-I
Technischer Platz: 1000154984-HN00
ONS-Bezeichnung: 
KVS-Bezeichnung: 
Hersteller: Corinex
Equipmentnr.: 217641322
Buchungskreis: 3190
Planergruppe: 45
Adresse: 

93449 Waldmünchen - Haschaberg

Materialnummer: 
Beschreibung: 
Repeater in ONS TH310275 (Haschaberg 33) von Stromkreis 11 auf Stromkreis 4 umkoppeln.
Buchen auf Investauftrag ON 922000084737`;

  const sampleCSV = `Ort	Ticket	Seriennummer	Bezeichnung	Straße	Mangel	LAT	LON	Zusatz	bearbeitet	Tätigkeit	Zeit am Gerät
93449 Waldmünchen - Haschaberg	20241002-0316	C100810230208099	KVS 90588	Haschaberg 13	Repeater meldet sich nicht	48.084734	12.246687	Bitte den KVS 90589 auch überprüfen	10.01.2025 Siener	Austausch	10min
83547 Babensham - Bergham	20240924-0627	C100810230200617	KVS 21639	Bergham 1	Repeater defekt	48.084734097	12.246687367	Repeater meldet sich nicht	10.10.2024 Siener	Austausch	15 min`;

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #CC0000 0%, #8B0000 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
        Ticket Import
      </Typography>

      <Paper sx={{ p: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="E-Mail Import" />
          <Tab label="CSV/Excel Import" />
        </Tabs>

        <Box sx={{ mt: 3 }}>
          {tabValue === 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                E-Mail-Text einfügen
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Fügen Sie den kompletten E-Mail-Text ein. Das System extrahiert automatisch alle relevanten Felder.
              </Typography>

              <Box
                component="div"
                contentEditable
                onPaste={handlePaste}
                onInput={(e) => {
                  const content = DOMPurify.sanitize(e.currentTarget.innerHTML, { ADD_TAGS: ['img'], ADD_ATTR: ['src'] });
                  setEmailContent(content);
                }}
                sx={{
                  width: '100%',
                  minHeight: { xs: '200px', sm: '300px' },
                  maxHeight: { xs: '350px', sm: '500px' },
                  overflow: 'auto',
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: 1,
                  padding: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                  '&:focus': {
                    outline: '2px solid #1976d2',
                    outlineOffset: -1
                  },
                  '& img': {
                    maxWidth: '100%',
                    height: 'auto',
                    margin: '8px 0'
                  },
                  '&:empty:before': {
                    content: '"E-Mail-Inhalt hier einfügen (Bilder werden automatisch mit übernommen)..."',
                    color: 'rgba(0, 0, 0, 0.6)'
                  }
                }}
                dangerouslySetInnerHTML={{ __html: emailContent }}
              />
              
              {pastedImages.length > 0 && (
                <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    Bilder für Import ausgewählt ({pastedImages.length}):
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {pastedImages.map((img, index) => (
                      <Box key={index} sx={{ position: 'relative' }}>
                        <img 
                          src={img.dataUrl} 
                          alt={`Pasted ${index + 1}`}
                          style={{ 
                            width: 100, 
                            height: 100, 
                            objectFit: 'cover',
                            border: '2px solid #1976d2',
                            borderRadius: 4
                          }}
                        />
                        <Button
                          size="small"
                          sx={{ 
                            position: 'absolute', 
                            top: -8, 
                            right: -8,
                            minWidth: 24,
                            width: 24,
                            height: 24,
                            padding: 0,
                            backgroundColor: 'error.main',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'error.dark'
                            }
                          }}
                          onClick={() => removeImage(index)}
                          title="Bild nicht importieren"
                        >
                          ×
                        </Button>
                      </Box>
                    ))}
                  </Box>
                  <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block', fontWeight: 'bold' }}>
                    ✓ Nur diese Bilder werden als Anhänge importiert. Gelöschte Bilder werden NICHT hochgeladen.
                  </Typography>
                </Box>
              )}

              <Box display="flex" gap={2} mb={2}>
                <Button
                  variant="outlined"
                  startIcon={<Preview />}
                  onClick={handleEmailPreview}
                  disabled={loading}
                >
                  Vorschau
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Send />}
                  onClick={handleEmailImport}
                  disabled={loading || !emailContent}
                >
                  Ticket erstellen
                </Button>
                <Button
                  variant="text"
                  onClick={() => setEmailContent(sampleEmail)}
                >
                  Beispiel laden
                </Button>
              </Box>

              {preview && (
                <Card sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Vorschau
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle1">
                      <strong>Titel:</strong> {preview.preview.title}
                    </Typography>
                    <Typography variant="subtitle1">
                      <strong>Priorität:</strong> 
                      <Chip 
                        label={preview.preview.priority} 
                        size="small" 
                        sx={{ ml: 1 }}
                        color={preview.preview.priority === 'high' ? 'error' : 'default'}
                      />
                    </Typography>
                    <Typography variant="subtitle1">
                      <strong>Kategorie:</strong> {preview.preview.category}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>
                      <strong>Beschreibung:</strong><br />
                      {preview.preview.description}
                    </Typography>
                    {preview.parsed.images && preview.parsed.images.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle1">
                          <strong>Gefundene Bilder:</strong>
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                          {preview.parsed.images.map((img, idx) => (
                            <Chip
                              key={idx}
                              icon={<Image />}
                              label={img.name}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                          Diese Bilder werden automatisch als Anhänge zum Ticket hinzugefügt
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {tabValue === 1 && (
            <>
              <Typography variant="h6" gutterBottom>
                CSV/Excel-Daten einfügen
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Kopieren Sie die Daten aus Excel mit Tab-Trennung. Die erste Zeile sollte die Spaltenüberschriften enthalten.
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={15}
                variant="outlined"
                placeholder="CSV/Excel-Daten hier einfügen (Tab-getrennt)..."
                value={csvContent}
                onChange={(e) => setCsvContent(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  startIcon={<CloudUpload />}
                  onClick={handleCSVImport}
                  disabled={loading || !csvContent}
                >
                  Tickets importieren
                </Button>
                <Button
                  variant="text"
                  onClick={() => setCsvContent(sampleCSV)}
                >
                  Beispiel laden
                </Button>
              </Box>
            </>
          )}
        </Box>

        {loading && <LinearProgress sx={{ mt: 2 }} />}

        {importResults && (
          <Alert 
            severity={importResults.success ? 'success' : 'error'}
            sx={{ mt: 2 }}
            icon={importResults.success ? <CheckCircle /> : <Error />}
          >
            {importResults.success ? (
              <>
                {importResults.ticketId ? (
                  <>
                    <Typography>
                      Ticket #{importResults.ticketId} - {importResults.title} wurde erfolgreich erstellt
                    </Typography>
                    {importResults.attachments && importResults.attachments.length > 0 && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <Image sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                        {importResults.attachments.length} {importResults.attachments.length === 1 ? 'Bild' : 'Bilder'} als Anhänge hinzugefügt
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography>
                    {importResults.created} Tickets erfolgreich importiert
                    {importResults.failed > 0 && ` (${importResults.failed} fehlgeschlagen)`}
                  </Typography>
                )}
                {importResults.errors && importResults.errors.length > 0 && (
                  <List dense>
                    {importResults.errors.map((err, idx) => (
                      <ListItem key={idx}>
                        <ListItemText 
                          primary={err.ticket}
                          secondary={err.error}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            ) : (
              <Typography>{importResults.error}</Typography>
            )}
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default ImportTickets;